import React, { useEffect, useMemo, useState } from "react";
import { Check, Search, Users, X } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { ScrollArea } from "../ui/scroll-area";
import { StaffAvatar } from "../StaffAvatar";
import { api, errMsg } from "../../lib/api";
import { toast } from "sonner";

/**
 * Phase 218 — "Who must sign" editor.
 * value = { all_staff: bool, roles: [role key | custom role id], user_ids: [user id] }
 *  • "All staff" switch (turns the detailed choice off)
 *  • role groups as toggle chips (Tax Collectors, Supervisors, Municipal Admins, Parking Marshals, custom roles)
 *  • individual staff with a search box, "Select all" (the filtered list) and "Clear all"
 *  • every selected role / person is a chip with a one-click X
 */
export const EMPTY_ASSIGNMENT = { all_staff: false, roles: [], user_ids: [] };

export function useStaffDirectory(code, enabled = true) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!code || !enabled) return undefined;
    let alive = true;
    api.get(`/t/${code}/policies/staff`).then((r) => { if (alive) setData(r.data); }).catch((e) => { if (alive) { toast.error(errMsg(e)); setData({ staff: [], roles: [] }); } });
    return () => { alive = false; };
  }, [code, enabled]);
  return data;
}

/** Which staff are covered by an assignment (for the live count). */
export function coveredStaff(staff, value) {
  if (!staff) return [];
  if (value.all_staff) return staff;
  return staff.filter((s) => value.roles.includes(s.role) || (s.custom_role_id && value.roles.includes(s.custom_role_id)) || value.user_ids.includes(s.id));
}

export function SignersPicker({ code, value, onChange, directory, testIdPrefix = "signers" }) {
  const fetched = useStaffDirectory(code, !directory);
  const dir = directory || fetched;
  const [q, setQ] = useState("");
  const staff = useMemo(() => (dir?.staff || []).filter((s) => s.is_active !== false), [dir]);
  const roles = dir?.roles || [];
  const v = { ...EMPTY_ASSIGNMENT, ...(value || {}) };
  const set = (patch) => onChange({ ...v, ...patch });

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return staff;
    return staff.filter((u) => [u.name, u.employee_id, u.email, u.designation, u.role_label, u.custom_role_name].some((f) => (f || "").toLowerCase().includes(s)));
  }, [staff, q]);
  const covered = coveredStaff(staff, v);
  const coveredIds = new Set(covered.map((s) => s.id));
  const byId = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s])), [staff]);
  const roleLabel = (key) => roles.find((r) => r.key === key)?.label || key;

  const toggleRole = (key) => set({ all_staff: false, roles: v.roles.includes(key) ? v.roles.filter((r) => r !== key) : [...v.roles, key] });
  const toggleUser = (id) => set({ all_staff: false, user_ids: v.user_ids.includes(id) ? v.user_ids.filter((u) => u !== id) : [...v.user_ids, id] });
  const selectAllFiltered = () => set({ all_staff: false, user_ids: Array.from(new Set([...v.user_ids, ...filtered.map((u) => u.id)])) });
  const clearAll = () => onChange({ ...EMPTY_ASSIGNMENT });

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-picker`}>
      <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 bg-muted/30">
        <div className="flex items-center gap-2 min-w-0">
          <Users className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <Label htmlFor={`${testIdPrefix}-all`} className="text-sm font-medium cursor-pointer">All staff</Label>
            <p className="text-[11px] text-muted-foreground">Every active staff account, including people added later ({staff.length} today). Citizens are never included.</p>
          </div>
        </div>
        <Switch id={`${testIdPrefix}-all`} checked={v.all_staff} onCheckedChange={(on) => set(on ? { all_staff: true, roles: [], user_ids: [] } : { all_staff: false })}
          data-testid={`${testIdPrefix}-all-staff-switch`} />
      </div>

      <div className={v.all_staff ? "opacity-50 pointer-events-none select-none" : ""} aria-disabled={v.all_staff}>
        <p className="text-xs font-medium mb-1.5">Roles</p>
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-roles`}>
          {roles.map((r) => {
            const on = v.roles.includes(r.key);
            return (
              <button key={r.key} type="button" onClick={() => toggleRole(r.key)} aria-pressed={on} data-testid={`${testIdPrefix}-role-${r.key}`}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"}`}>
                {on && <Check className="h-3 w-3" />}{r.label}<span className={`ml-0.5 ${on ? "opacity-80" : "text-muted-foreground"}`}>({r.count})</span>
              </button>
            );
          })}
          {roles.length === 0 && <span className="text-xs text-muted-foreground">Loading roles...</span>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 mt-4 mb-1.5">
          <p className="text-xs font-medium">Individual staff</p>
          <div className="flex items-center gap-1">
            <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={selectAllFiltered} disabled={filtered.length === 0} data-testid={`${testIdPrefix}-select-all`}>
              Select all{q.trim() ? ` (${filtered.length} found)` : ""}
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={clearAll} disabled={!v.all_staff && v.roles.length === 0 && v.user_ids.length === 0} data-testid={`${testIdPrefix}-clear-all`}>
              Clear all
            </Button>
          </div>
        </div>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, employee ID, email or designation..." className="pl-8 h-9" data-testid={`${testIdPrefix}-search`} />
        </div>
        <ScrollArea className="h-56 rounded-md border mt-2">
          <ul className="divide-y" data-testid={`${testIdPrefix}-staff-list`}>
            {!dir ? <li className="px-3 py-3 text-xs text-muted-foreground">Loading staff...</li>
              : filtered.length === 0 ? <li className="px-3 py-3 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-no-match`}>No staff match "{q}"</li>
              : filtered.map((u) => {
                const picked = v.user_ids.includes(u.id);
                const viaRole = !picked && coveredIds.has(u.id);
                return (
                  <li key={u.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-muted/50" data-testid={`${testIdPrefix}-staff-row-${u.id}`}>
                    <Checkbox checked={picked} onCheckedChange={() => toggleUser(u.id)} id={`${testIdPrefix}-u-${u.id}`} data-testid={`${testIdPrefix}-staff-check-${u.id}`} aria-label={`Select ${u.name}`} />
                    <StaffAvatar code={code} userId={u.id} name={u.name} photoUrl={u.photo_url} size="xs" />
                    <label htmlFor={`${testIdPrefix}-u-${u.id}`} className="flex-1 min-w-0 cursor-pointer">
                      <p className="text-sm truncate">{u.name} {u.employee_id && <span className="font-mono text-[11px] text-muted-foreground">({u.employee_id})</span>}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{u.custom_role_name || u.role_label}{u.designation ? ` • ${u.designation}` : ""}</p>
                    </label>
                    {viaRole && <Badge variant="outline" className="text-[10px] shrink-0" title="Already covered by a selected role">via role</Badge>}
                  </li>
                );
              })}
          </ul>
        </ScrollArea>
      </div>

      <div className="rounded-lg border px-3 py-2.5" data-testid={`${testIdPrefix}-summary`}>
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <p className="text-xs font-medium">Selected</p>
          <Badge variant="secondary" className="text-[10px] tabular-nums" data-testid={`${testIdPrefix}-covered-count`}>{covered.length} staff must sign</Badge>
        </div>
        {v.all_staff ? (
          <Chip label="All staff" onRemove={() => set({ all_staff: false })} testId={`${testIdPrefix}-chip-all`} />
        ) : v.roles.length === 0 && v.user_ids.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-nobody`}>Nobody selected yet — nobody will see or be asked to sign this policy.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {v.roles.map((r) => <Chip key={r} label={roleLabel(r)} onRemove={() => toggleRole(r)} testId={`${testIdPrefix}-chip-role-${r}`} />)}
            {v.user_ids.map((id) => <Chip key={id} label={byId[id]?.name || "Staff member"} sub={byId[id]?.employee_id} onRemove={() => toggleUser(id)} testId={`${testIdPrefix}-chip-user-${id}`} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function Chip({ label, sub, onRemove, testId }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary border border-primary/20 pl-2.5 pr-1 py-0.5 text-xs" data-testid={testId}>
      {label}{sub && <span className="font-mono text-[10px] opacity-70">({sub})</span>}
      <button type="button" onClick={onRemove} className="rounded-full p-0.5 hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={`Remove ${label}`} data-testid={`${testId}-remove`}>
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}
